import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Check, Image, Car, Lock, Bell, DollarSign } from "lucide-react";
import { notificationService } from "@/services/notificationService";
import { NOTIFICATION_TYPES, getNotificationRoute } from "@/utils/notificationTypes";

const getNotificationIcon = (type: string) => {
  switch (type) {
    case NOTIFICATION_TYPES.PAYMENT:
      return { icon: DollarSign, bg: "#EFF6FF", color: "#2563EB" };
    case NOTIFICATION_TYPES.SHIPMENT:
      return { icon: Car, bg: "#F9FAFB", color: "#667185" };
    case NOTIFICATION_TYPES.ACCOUNT:
      return { icon: Lock, bg: "#EFF6FF", color: "#2563EB" };
    default:
      return { icon: Bell, bg: "#F9FAFB", color: "#667185" };
  }
};

interface NotificationsPanelProps {
  onClose: () => void;
}

export default function NotificationsPanel({ onClose }: NotificationsPanelProps) {
  const [activeTab, setActiveTab] = useState<"all" | "unread">("all");
  const [notifications, setNotifications] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      setIsLoading(true);
      const data = await notificationService.getNotifications();
      setNotifications(data || []);
    } catch (error) {
      console.error("Failed to load notifications:", error);
      setNotifications([]);
    } finally {
      setIsLoading(false);
    }
  };

  const unreadNotifications = notifications.filter((n: any) => !n.read);
  const unreadCount = unreadNotifications.length;

  const markAllRead = async () => {
    const unreadIds = unreadNotifications.map((n: any) => n.id);
    if (unreadIds.length > 0) {
      try {
        await notificationService.markNotificationsAsRead(unreadIds);
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      } catch (error) {
        console.error("Failed to mark all notifications as read:", error);
      }
    }
  };

  const markOneRead = async (id: number) => {
    try {
      await notificationService.markNotificationsAsRead([id]);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const handleNotificationClick = (notification: any) => {
    const route = getNotificationRoute(notification.type, { shipmentId: notification.listing?.id });
    navigate(route);
    onClose();
  };

  const displayed = activeTab === "unread" ? unreadNotifications : notifications;

  return (
    <div className="fixed inset-0 z-[1400] flex" onClick={onClose}>
      {/* Backdrop — stronger blur on desktop; lighter on mobile */}
      <div className="absolute inset-0 bg-black/10" />

      {/* Panel */}
      <div
        className="relative z-10 flex h-full w-full flex-col bg-white shadow-2xl border border-[#D0D5DD] md:ml-[210px] md:mt-3 md:mb-3 md:h-auto md:w-[400px] md:max-h-[calc(100vh-24px)] md:rounded-[16px] md:overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-10 border-b border-[#E4E7EC] ">
          <h2 className="text-[18px] font-[600] text-[#101928]">Notifications</h2>
          <div className="flex gap-2 items-center">
            <button
              onClick={markAllRead}
              className="flex items-center  -py-2 gap-1.5 border border-[#D4D4D4] rounded-full px-3  text-[12px] font-[600] text-[#0A0A0A] hover:bg-[#F9FAFB] transition-colors"
            >
              <Check size={12} />
              Mark all as read
            </button>
            <button
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-[#F0F2F5] transition-colors text-[#667185]"
            >
              <X size={16} color="#0A0A0A" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex justify-evenly  items-center gap-0 px-5 border-b border-[#E4E7EC]">
          {(["all", "unread"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex w-1/2  justify-center item-center items-center gap-1.5 py-3 px-4 text-[14px] font-[500] border-b-2 transition-colors capitalize ${
                activeTab === tab
                  ? "border-[#2563EB] text-[#2563EB]"
                  : "border-transparent text-[#667185] hover:text-[#344054]"
              }`}
            >
              {tab === "all" ? "All" : "Unread"}
              {tab === "unread" && unreadCount > 0 && (
                <span className="bg-[#2563EB] text-white text-[11px] font-[600] rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
              {tab === "unread" && unreadCount === 0 && (
                <span className="bg-[#F0F2F5] text-[#667185] text-[11px] font-[600] rounded-full w-5 h-5 flex items-center justify-center">
                  0
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1">
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563EB]"></div>
            </div>
          ) : displayed.length === 0 ? (
            <div className="flex flex-col justify-center items-center px-6 py-20 text-center">
              <div className="w-12 h-12 rounded-full bg-[#F0F2F5] flex items-center justify-center mb-4">
                <Bell size={22} className="text-[#98A2B3]" />
              </div>
              <p className="text-[15px] font-[500] text-[#344054] mb-1">No notifications yet</p>
              <p className="text-[13px] text-[#667185]">Get notified about your shipments when you begin one</p>
            </div>
          ) : (
            <div className="py-4">
              <p className="text-[12px] font-[600] tracking-widest text-[#344054] mb-3 px-3">TODAY</p>
              <div className="flex flex-col">
                {displayed.map((notif: any) => {
                  const iconConfig = getNotificationIcon(notif.type);
                  const Icon = iconConfig.icon;
                  return (
                    <div
                      key={notif.id}
                      className={`${!notif.read ? "bg-[#F9FAFB]" : "#fff"}  p-3 transition-color flex gap-3 mb-[2px] cursor-pointer hover:bg-[#F9FAFB]`}
                      onClick={() => handleNotificationClick(notif)}
                    >
                      {/* Icon */}
                      <div className="relative flex-shrink-0">
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center border-[0.5px] border-[#D4D4D4]"
                          style={{ backgroundColor: "transparent" }}
                        >
                          <Icon size={16} />
                        </div>
                        {!notif.read && (
                          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-[#2563EB] rounded-full border-2 border-white" />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-[600] text-[#11121A] mb-0.5">{notif.title}</p>
                        <p className="text-[12px] text-[#737373] leading-[1.5] mb-1">{notif.message}</p>
                        <p className="text-[12px] text-[#98A2B3] mb-2">
                          {new Date(notif.created_at).toLocaleDateString()}
                        </p>
                        <div className="flex gap-2 items-center">
                          {!notif.read && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                markOneRead(notif.id);
                              }}
                              className="flex items-center gap-1.5 border border-[#D4D4D4] rounded-full px-3  text-[12px] font-[600] text-[#0A0A0A] hover:bg-[#F9FAFB] transition-colors"
                            >
                              Mark as Read
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
